// ============================================================================
// IVR API Service
// Complete API service layer for IVR management
// ============================================================================

import type {
  IVRConfig,
  IVRNode,
  StateDetail,
  Organization,
  Business,
  VIPList,
  VIPCaller,
  DNDList,
  DNDEntry,
  CallRecord,
  APIResponse,
  PaginatedResponse,
  DeploymentResult,
  ValidationResult,
  FlowIdentifier,
} from '../types/ivr';
import { deployFlow, buildStates } from '../api/services/ivrDeploy.service';
import { stateDetailsService } from '../api/services/stateDetails.service';
import axiosInstance from '../api/axios.config';
import { responseFrom, errorFrom } from '../api/apiEnvelope';

// Base API URL - adjust based on your backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// ----------------------------------------------------------------------------
// Helper Functions
// ----------------------------------------------------------------------------

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || 'Request failed',
      };
    }

    return {
      success: true,
      data: data.data || data,
      message: data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

async function fetchPaginated<T>(
  endpoint: string,
  params?: Record<string, any>
): Promise<PaginatedResponse<T>> {
  try {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}${endpoint}${queryString}`);

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      };
    }

    return {
      success: true,
      data: data.data || [],
      pagination: data.pagination || {
        page: 1,
        limit: 10,
        total: data.data?.length || 0,
        totalPages: 1,
      },
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
    };
  }
}

/**
 * One cpass call over the authenticated axios client, reported as APIResponse.
 *
 * fetchAPI above is bare `fetch`. It sends Content-Type and nothing else — no
 * Authorization, no X-Organization-Id — so every call it makes comes back 401.
 * Worse, that 401 never passes through the axios response interceptor, so the
 * session-expiry handling (drop the token, send the user to /login) never runs
 * and the user is left staring at a toast reading "Authentication is required
 * for this request." Anything routed through here gets the headers on the way
 * out and the interceptor on the way back.
 */
async function apiCall<T>(
  send: () => Promise<{ data: any }>,
  fallback: string
): Promise<APIResponse<T>> {
  try {
    return responseFrom<T>((await send()).data);
  } catch (e) {
    return errorFrom<T>(e, fallback);
  }
}

// ----------------------------------------------------------------------------
// IVR Configuration API
// ----------------------------------------------------------------------------

export const ivrApi = {
  /**
   * Get all IVR configurations
   */
  getAll: async (params?: {
    page?: number;
    limit?: number;
    business_id?: string;
    status?: 'draft' | 'active' | 'inactive';
  }): Promise<PaginatedResponse<IVRConfig>> => {
    return fetchPaginated<IVRConfig>('/ivr', params);
  },

  /**
   * Get single IVR configuration by ID
   */
  getById: async (id: string): Promise<APIResponse<IVRConfig>> => {
    return fetchAPI<IVRConfig>(`/ivr/${id}`);
  },

  /**
   * Create new IVR configuration
   */
  create: async (config: Partial<IVRConfig>): Promise<APIResponse<IVRConfig>> => {
    return fetchAPI<IVRConfig>('/ivr', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  /**
   * Update IVR configuration
   */
  update: async (id: string, config: Partial<IVRConfig>): Promise<APIResponse<IVRConfig>> => {
    return fetchAPI<IVRConfig>(`/ivr/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  /**
   * Delete IVR configuration
   */
  delete: async (id: string): Promise<APIResponse<void>> => {
    return fetchAPI<void>(`/ivr/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Deploy IVR to production (legacy - by ID)
   */
  deploy: async (id: string): Promise<APIResponse<{ deployed: boolean; message: string }>> => {
    return fetchAPI<{ deployed: boolean; message: string }>(`/ivr/${id}/deploy`, {
      method: 'POST',
    });
  },

  /**
   * Deploy IVR flow to state machine (Option 1 - Bridge approach)
   *
   * Transforms frontend nodes to backend state_details rows
   * that can be executed by the AGI script.
   *
   * @param config IVR configuration from the visual builder
   * @param organizationId Organization ID for tenant routing
   * @returns Deployment result with stats
   */
  deployToStateMachine: async (
    config: IVRConfig,
    organizationId: string
  ): Promise<APIResponse<DeploymentResult>> => {
    // The legacy /ivr/state-details/deploy endpoint has no handler on this
    // backend. Translate the node graph client-side and upsert each state
    // through the existing, authenticated /telephony/ivr/state-details CRUD.
    const cfg: any = config;
    const result = await deployFlow({
      name: config.name,
      operatorId: config.business?.operator_id || cfg.operator_id || 'default',
      serviceId: config.business?.service_id || cfg.service_id || 'default',
      businessId: config.business_id || organizationId,
      businessName: (config.business as any)?.name,
      language: config.language || 'en',
      nodes: config.nodes,
    });
    return result.success
      ? { success: true, data: result.data as unknown as DeploymentResult, message: result.data?.message }
      : { success: false, error: result.error };
  },

  /**
   * Validate IVR flow before deployment
   */
  validateFlow: async (config: IVRConfig): Promise<APIResponse<ValidationResult>> => {
    // Client-side validation — there is no backend validate endpoint. Reuse the
    // same translation the deploy uses, so its warnings/errors are surfaced
    // before anything is written.
    const cfg: any = config;
    const errors: string[] = [];
    if (!config.name) errors.push('IVR name is required');
    const operatorId = config.business?.operator_id || cfg.operator_id;
    const serviceId = config.business?.service_id || cfg.service_id;
    if (!operatorId) errors.push('Operator ID is required');
    if (!serviceId) errors.push('Service ID is required');
    if (!config.nodes || config.nodes.length === 0) errors.push('Flow has no nodes');

    const { states, warnings } = buildStates({
      name: config.name,
      operatorId: operatorId || 'default',
      serviceId: serviceId || 'default',
      language: config.language || 'en',
      nodes: config.nodes || [],
    });
    return {
      success: true,
      data: { valid: errors.length === 0, errors, warnings, stateCount: states.length } as unknown as ValidationResult,
    };
  },

  /**
   * Get deployed states for an IVR flow
   */
  getDeployedStates: async (
    organizationId: string,
    operatorId: string,
    serviceId: string,
    language: string = 'en'
  ): Promise<APIResponse<StateDetail[]>> => {
    // Read deployed states from the existing authenticated CRUD endpoint, then
    // filter by language client-side (the list API keys on operator/service).
    const res = await stateDetailsService.list({ operatorId, serviceId });
    if (!res.success) return { success: false, error: res.error };
    const rows = (res.data || []).filter(r => (r.language || 'en') === language);
    return { success: true, data: rows as unknown as StateDetail[] };
  },

  /**
   * Get all deployed flows for an organization
   */
  getDeployedFlows: async (
    organizationId: string
  ): Promise<APIResponse<FlowIdentifier[]>> => {
    return fetchAPI<FlowIdentifier[]>(
      `/ivr/state-details/flows?organizationId=${encodeURIComponent(organizationId)}`
    );
  },

  /**
   * Get state details for IVR
   */
  getStates: async (id: string, language?: string): Promise<APIResponse<StateDetail[]>> => {
    const queryString = language ? `?language=${language}` : '';
    return fetchAPI<StateDetail[]>(`/ivr/${id}/states${queryString}`);
  },

  /**
   * Batch create/update state details
   */
  updateStates: async (
    id: string,
    states: StateDetail[]
  ): Promise<APIResponse<{ created: number; updated: number }>> => {
    return fetchAPI<{ created: number; updated: number }>(`/ivr/${id}/states/batch`, {
      method: 'POST',
      body: JSON.stringify({ states }),
    });
  },

  /**
   * Test IVR configuration
   */
  test: async (id: string, phoneNumber: string): Promise<APIResponse<{ test_call_id: string }>> => {
    return fetchAPI<{ test_call_id: string }>(`/ivr/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  },

  /**
   * Clone IVR configuration
   */
  clone: async (id: string, newName: string): Promise<APIResponse<IVRConfig>> => {
    return fetchAPI<IVRConfig>(`/ivr/${id}/clone`, {
      method: 'POST',
      body: JSON.stringify({ name: newName }),
    });
  },
};

// ----------------------------------------------------------------------------
// Organization API
// ----------------------------------------------------------------------------

export const organizationApi = {
  /**
   * Get all organizations
   */
  getAll: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Organization>> => {
    return fetchPaginated<Organization>('/organizations', params);
  },

  /**
   * Get organization by ID
   */
  getById: async (id: string): Promise<APIResponse<Organization>> => {
    return fetchAPI<Organization>(`/organizations/${id}`);
  },

  /**
   * Create organization
   */
  create: async (org: Omit<Organization, 'id'>): Promise<APIResponse<Organization>> => {
    return fetchAPI<Organization>('/organizations', {
      method: 'POST',
      body: JSON.stringify(org),
    });
  },

  /**
   * Update organization
   */
  update: async (id: string, updates: Partial<Organization>): Promise<APIResponse<Organization>> => {
    return fetchAPI<Organization>(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete organization
   */
  delete: async (id: string): Promise<APIResponse<void>> => {
    return fetchAPI<void>(`/organizations/${id}`, {
      method: 'DELETE',
    });
  },
};

// ----------------------------------------------------------------------------
// Business API
// ----------------------------------------------------------------------------

export const businessApi = {
  // Rewired to the cpass orgs API (/organizations/businesses) via the authenticated axios
  // client. Maps Organization -> Business shape; operator_id/service_id pass through if
  // present (the BusinessSelector hides them gracefully when absent).
  getAll: async (_params?: { organization_id?: string; page?: number; size?: number }): Promise<PaginatedResponse<Business>> => {
    try {
      const { data } = await axiosInstance.get('/organizations/businesses');
      const rows = (data?.data || data || []).map((o: any) => ({ ...o, organization_id: o.id }));
      return { success: true, data: rows, total: rows.length, page: 1, perPage: rows.length } as any;
    } catch (e: any) {
      return { success: false, error: e?.response?.data?.message || 'Failed to load businesses', data: [] } as any;
    }
  },
  getById: async (id: string): Promise<APIResponse<Business>> => {
    try { const { data } = await axiosInstance.get(`/organizations/${id}`); const o = data?.data || data; return { success: true, data: { ...o, organization_id: o?.id } } as any; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Business not found' }; }
  },
  update: async (id: string, updates: Partial<Business>): Promise<APIResponse<Business>> => {
    try { const { data } = await axiosInstance.patch(`/organizations/${id}`, updates); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Update failed' }; }
  },
  getByOrganization: async (_organizationId: string): Promise<PaginatedResponse<Business>> => {
    return businessApi.getAll();
  },
};

// ----------------------------------------------------------------------------
// VIP List Management API
// ----------------------------------------------------------------------------

export const vipListApi = {
  /**
   * Get all VIP lists
   */
  getAll: async (params?: {
    business_id?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<VIPList>> => {
    return fetchPaginated<VIPList>('/vip-lists', params);
  },

  /**
   * Get VIP list by ID
   */
  getById: async (id: number): Promise<APIResponse<VIPList>> => {
    return fetchAPI<VIPList>(`/vip-lists/${id}`);
  },

  /**
   * Create new VIP list
   */
  create: async (list: Omit<VIPList, 'id'>): Promise<APIResponse<VIPList>> => {
    return fetchAPI<VIPList>('/vip-lists', {
      method: 'POST',
      body: JSON.stringify(list),
    });
  },

  /**
   * Update VIP list
   */
  update: async (id: number, updates: Partial<VIPList>): Promise<APIResponse<VIPList>> => {
    return fetchAPI<VIPList>(`/vip-lists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete VIP list
   */
  delete: async (id: number): Promise<APIResponse<void>> => {
    return fetchAPI<void>(`/vip-lists/${id}`, {
      method: 'DELETE',
    });
  },
};

// ----------------------------------------------------------------------------
// VIP Caller Management API
// Matches backend VipController which expects organizationId as query parameter
// ----------------------------------------------------------------------------

export const vipApi = {
  // Rewired to the cpass VIP API (/vip) via the authenticated axios client.
  getAll: async (organizationId: string): Promise<APIResponse<VIPCaller[]>> => {
    try { const { data } = await axiosInstance.get('/vip', { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to load VIP list' }; }
  },
  getById: async (_organizationId: string, id: number): Promise<APIResponse<VIPCaller>> => {
    try { const { data } = await axiosInstance.get(`/vip/${id}`); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Not found' }; }
  },
  search: async (organizationId: string, params: { page?: number; size?: number; query?: string } = {}): Promise<APIResponse<any>> => {
    try {
      const { data } = await axiosInstance.get('/vip', { params: { organizationId, q: params.query || undefined } });
      const rows = data?.data || [];
      return { success: true, data: { content: rows, totalPages: 1, totalElements: rows.length } };
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Search failed' }; }
  },
  check: async (organizationId: string, phoneNumber: string): Promise<APIResponse<any>> => {
    try { const { data } = await axiosInstance.get('/vip/is-vip', { params: { organizationId, phoneNumber } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Check failed' }; }
  },
  isVip: async (organizationId: string, phoneNumber: string): Promise<APIResponse<any>> => {
    try { const { data } = await axiosInstance.get('/vip/is-vip', { params: { organizationId, phoneNumber } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Check failed' }; }
  },
  create: async (organizationId: string, vip: { phoneNumber: string; vipName?: string; priorityLevel?: number; notes?: string }): Promise<APIResponse<VIPCaller>> => {
    try {
      const { data } = await axiosInstance.post('/vip', {
        msisdn: vip.phoneNumber,
        vip_level: vip.priorityLevel,
        priority: vip.priorityLevel,
        notes: vip.notes ?? vip.vipName,
        channels: ['VOICE'],
      }, { params: { organizationId } });
      return data;
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Create failed' }; }
  },
  update: async (_organizationId: string, id: number, updates: { phoneNumber?: string; vipName?: string; priorityLevel?: number; notes?: string; active?: boolean }): Promise<APIResponse<VIPCaller>> => {
    try {
      const { data } = await axiosInstance.put(`/vip/${id}`, {
        msisdn: updates.phoneNumber,
        vip_level: updates.priorityLevel,
        notes: updates.notes ?? updates.vipName,
        active: updates.active,
      });
      return data;
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Update failed' }; }
  },
  delete: async (_organizationId: string, id: number): Promise<APIResponse<void>> => {
    try { const { data } = await axiosInstance.delete(`/vip/${id}`); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Delete failed' }; }
  },
  bulkImport: async (_organizationId: string, _file: File): Promise<APIResponse<any>> => {
    return { success: false, error: 'Bulk import is not available yet' };
  },
  export: async (organizationId: string): Promise<Blob | null> => {
    try {
      const { data } = await axiosInstance.get('/vip', { params: { organizationId } });
      const rows = data?.data || [];
      const csv = 'msisdn,vip_level,priority,notes,is_active\n' +
        rows.map((r: any) => [r.msisdn, r.vip_level, r.priority, (r.notes || '').replace(/,/g, ';'), r.is_active].join(',')).join('\n');
      return new Blob([csv], { type: 'text/csv' });
    } catch { return null; }
  },
};
export const dndListApi = {
  /**
   * Get all DND lists
   */
  getAll: async (params?: {
    business_id?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<DNDList>> => {
    return fetchPaginated<DNDList>('/dnd-lists', params);
  },

  /**
   * Get DND list by ID
   */
  getById: async (id: number): Promise<APIResponse<DNDList>> => {
    return fetchAPI<DNDList>(`/dnd-lists/${id}`);
  },

  /**
   * Create new DND list
   */
  create: async (list: Omit<DNDList, 'id'>): Promise<APIResponse<DNDList>> => {
    return fetchAPI<DNDList>('/dnd-lists', {
      method: 'POST',
      body: JSON.stringify(list),
    });
  },

  /**
   * Update DND list
   */
  update: async (id: number, updates: Partial<DNDList>): Promise<APIResponse<DNDList>> => {
    return fetchAPI<DNDList>(`/dnd-lists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete DND list
   */
  delete: async (id: number): Promise<APIResponse<void>> => {
    return fetchAPI<void>(`/dnd-lists/${id}`, {
      method: 'DELETE',
    });
  },
};

// ----------------------------------------------------------------------------
// DND Entry Management API
// Matches backend DndController which expects organizationId as query parameter
// ----------------------------------------------------------------------------

// DND Channel type matching backend enum
export type DndChannel = 'VOICE' | 'SMS' | 'WHATSAPP' | 'EMAIL' | 'ALL';

export const dndApi = {
  // Rewired to the cpass DND API (/dnd) via the authenticated axios client.
  getAll: async (organizationId: string): Promise<APIResponse<DNDEntry[]>> => {
    try { const { data } = await axiosInstance.get('/dnd', { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to load DND list' }; }
  },
  getById: async (_organizationId: string, id: number): Promise<APIResponse<DNDEntry>> => {
    try { const { data } = await axiosInstance.get(`/dnd/${id}`); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Not found' }; }
  },
  // Paginated wrapper over the cpass list (single page) so the page's search() path works.
  search: async (organizationId: string, params: { page?: number; size?: number; query?: string } = {}): Promise<APIResponse<any>> => {
    try {
      const { data } = await axiosInstance.get('/dnd', { params: { organizationId, q: params.query || undefined } });
      const rows = data?.data || [];
      return { success: true, data: { content: rows, totalPages: 1, totalElements: rows.length } };
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Search failed' }; }
  },
  isBlocked: async (organizationId: string, opts: { identifier: string; channel?: string }): Promise<APIResponse<any>> => {
    try { const { data } = await axiosInstance.get('/dnd/is-blocked', { params: { organizationId, msisdn: opts.identifier, channel: opts.channel } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Check failed' }; }
  },
  create: async (organizationId: string, dndEntry: { phoneNumber?: string; identifier?: string; channel?: string; reason?: string; expiresAt?: string }): Promise<APIResponse<DNDEntry>> => {
    try {
      const { data } = await axiosInstance.post('/dnd', {
        msisdn: dndEntry.phoneNumber || dndEntry.identifier,
        channels: dndEntry.channel ? [dndEntry.channel] : ['VOICE'],
        block_reason: dndEntry.reason,
        expires_at: dndEntry.expiresAt,
      }, { params: { organizationId } });
      return data;
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Create failed' }; }
  },
  update: async (_organizationId: string, id: number, updates: { phoneNumber?: string; channel?: string; reason?: string; expiresAt?: string; active?: boolean }): Promise<APIResponse<DNDEntry>> => {
    try {
      const { data } = await axiosInstance.put(`/dnd/${id}`, {
        msisdn: updates.phoneNumber,
        channels: updates.channel ? [updates.channel] : undefined,
        block_reason: updates.reason,
        expires_at: updates.expiresAt,
        active: updates.active,
      });
      return data;
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Update failed' }; }
  },
  delete: async (_organizationId: string, id: number): Promise<APIResponse<void>> => {
    try { const { data } = await axiosInstance.delete(`/dnd/${id}`); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Delete failed' }; }
  },
  bulkImport: async (_organizationId: string, _file: File): Promise<APIResponse<any>> => {
    return { success: false, error: 'Bulk import is not available yet' };
  },
  export: async (organizationId: string): Promise<Blob | null> => {
    try {
      const { data } = await axiosInstance.get('/dnd', { params: { organizationId } });
      const rows = data?.data || [];
      const csv = 'msisdn,channels,block_reason,severity,active\n' +
        rows.map((r: any) => [r.msisdn, (r.channels || []).join('|'), (r.block_reason || '').replace(/,/g, ';'), r.severity, r.active].join(',')).join('\n');
      return new Blob([csv], { type: 'text/csv' });
    } catch { return null; }
  },
};

// ----------------------------------------------------------------------------
// Call Records / CDR API
// ----------------------------------------------------------------------------

export const callsApi = {
  /**
   * Get call records
   */
  getAll: async (params?: {
    business_id?: string;
    page?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
    is_vip?: boolean;
    was_blocked?: boolean;
  }): Promise<PaginatedResponse<CallRecord>> => {
    return fetchPaginated<CallRecord>('/calls', params);
  },

  /**
   * Get call details
   */
  getById: async (id: string): Promise<APIResponse<CallRecord>> => {
    return fetchAPI<CallRecord>(`/calls/${id}`);
  },

  /**
   * Get call statistics
   */
  getStats: async (params?: {
    business_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<APIResponse<{
    total_calls: number;
    completed_calls: number;
    failed_calls: number;
    avg_duration: number;
    total_duration: number;
  }>> => {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return fetchAPI(`/calls/stats${queryString}`);
  },

  /**
   * Get VIP caller statistics
   */
  getVIPStats: async (businessId: string): Promise<APIResponse<{
    total_vip_calls: number;
    vip_calls_by_level: Record<number, number>;
    avg_vip_call_duration: number;
  }>> => {
    return fetchAPI(`/calls/vip-stats?business_id=${businessId}`);
  },

  /**
   * Get DND blocking statistics
   */
  getDNDStats: async (businessId: string): Promise<APIResponse<{
    total_blocked: number;
    hard_blocks: number;
    soft_blocks: number;
    blocks_by_date: Array<{ date: string; count: number }>;
  }>> => {
    return fetchAPI(`/calls/dnd-stats?business_id=${businessId}`);
  },

  /**
   * Get context monitoring statistics
   */
  getContextStats: async (businessId: string): Promise<APIResponse<{
    total_ai_calls: number;
    avg_tokens_used: number;
    context_exits: number;
    avg_context_usage_pct: number;
  }>> => {
    return fetchAPI(`/calls/context-stats?business_id=${businessId}`);
  },
};

// ----------------------------------------------------------------------------
// Audio/TTS API
// ----------------------------------------------------------------------------

export const audioApi = {
  // Rewired to the cpass IVR audio API (/telephony/ivr/audio) via the authenticated axios
  // client. TTS generation needs a configured provider (separate integration) — stubbed.
  upload: async (file: File, metadata?: { organizationId?: string; name?: string; language?: string; business_id?: string }): Promise<APIResponse<{ url: string; path: string }>> => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params: any = { organizationId: metadata?.organizationId };
      if (metadata?.name) params.name = metadata.name;
      if (metadata?.language) params.language = metadata.language;
      if (metadata?.business_id) params.businessId = metadata.business_id;
      const { data } = await axiosInstance.post('/telephony/ivr/audio', fd, { params, headers: { 'Content-Type': 'multipart/form-data' } });
      const a = data?.data || data;
      return { success: data?.success !== false, data: { url: a?.url, path: a?.storage_path } } as any;
    } catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Upload failed' }; }
  },
  // Synthesize + persist a reusable IVR audio asset. Returns the asset (with `url`).
  // Backend returns 501 until a TTS provider is configured — surfaced as a clean error.
  generateTTS: async (params: { text: string; language?: string; voice?: string; organizationId?: string; business_id?: string; name?: string }): Promise<APIResponse<any>> => {
    try {
      const { data } = await axiosInstance.post('/telephony/ivr/audio/tts/generate', {
        text: params.text, language: params.language, voice: params.voice,
        organizationId: params.organizationId, businessId: params.business_id, name: params.name,
      });
      return data;
    } catch (e: any) {
      if (e?.response?.status === 501) return { success: false, error: e?.response?.data?.message || 'TTS is not configured yet — upload an audio file instead.' };
      return { success: false, error: e?.response?.data?.message || 'TTS generation failed' };
    }
  },
  // Synthesize without persisting — returns an object URL for the audio so the caller can play it.
  // Caller should URL.revokeObjectURL(url) when done. 501 until a provider is configured.
  previewTTS: async (params: { text: string; language?: string; voice?: string; organizationId?: string }): Promise<APIResponse<{ url: string }>> => {
    try {
      const res = await axiosInstance.post('/telephony/ivr/audio/tts/preview', {
        text: params.text, language: params.language, voice: params.voice, organizationId: params.organizationId,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      return { success: true, data: { url } };
    } catch (e: any) {
      // With responseType blob the error body is a Blob — read it back as text for the message.
      let msg = 'TTS preview failed';
      try { const b = e?.response?.data; if (b && typeof b.text === 'function') { const t = await b.text(); msg = JSON.parse(t)?.message || msg; } } catch { /* keep default */ }
      if (e?.response?.status === 501) return { success: false, error: msg || 'TTS is not configured yet — upload an audio file instead.' };
      return { success: false, error: msg };
    }
  },
  list: async (params?: { organizationId?: string }): Promise<APIResponse<any[]>> => {
    try { const { data } = await axiosInstance.get('/telephony/ivr/audio', { params: { organizationId: params?.organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to load audio' }; }
  },
  delete: async (id: number): Promise<APIResponse<void>> => {
    try { const { data } = await axiosInstance.delete(`/telephony/ivr/audio/${id}`); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Delete failed' }; }
  },
};

// ----------------------------------------------------------------------------
// Business Hours API
// Matches backend BusinessHoursController
// ----------------------------------------------------------------------------

export interface BusinessHoursDTO {
  id?: number;
  organizationId?: string;
  hoursByDay: Record<string, { startTime: string; endTime: string } | null>;
  timezone: string;
  enabled: boolean;
  afterHoursMessage?: string;
  afterHoursAudioUrl?: string;
  afterHoursAction?: 'HANGUP' | 'VOICEMAIL' | 'TRANSFER' | 'CUSTOM';
  afterHoursTransferNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BusinessHoursCheckResult {
  withinBusinessHours: boolean;
  currentDay: string;
  currentTime: string;
  timezone: string;
  todayHours?: { startTime: string; endTime: string };
  afterHoursAction?: string;
  afterHoursMessage?: string;
}

export interface BusinessHoursSyncDay {
  day: string;
  windowsSent: number;
  /** Counted in the RUNTIME's read-back, never in what was sent. */
  windowsConfirmed: number;
  detail: string | null;
}

export interface BusinessHoursSyncReport {
  businessId: string;
  /** True only when every window cpass holds was found on the runtime. */
  faithful: boolean;
  windowsSent: number;
  windowsConfirmed: number;
  days: BusinessHoursSyncDay[];
  failures: string[];
}

export const businessHoursApi = {
  // Rewired to the cpass business-hours API (/business-hours) via the
  // authenticated axios client. The DTO is camelCase and maps 1:1 to the entity.
  get: async (organizationId: string): Promise<APIResponse<BusinessHoursDTO>> => {
    try { const { data } = await axiosInstance.get('/business-hours', { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to load business hours' }; }
  },
  create: async (organizationId: string, businessHours: any): Promise<APIResponse<BusinessHoursDTO>> => {
    try { const { data } = await axiosInstance.post('/business-hours', businessHours, { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to save business hours' }; }
  },
  update: async (organizationId: string, businessHours: any): Promise<APIResponse<BusinessHoursDTO>> => {
    try { const { data } = await axiosInstance.put('/business-hours', businessHours, { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to save business hours' }; }
  },
  delete: async (organizationId: string): Promise<APIResponse<void>> => {
    try { const { data } = await axiosInstance.delete('/business-hours', { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to delete business hours' }; }
  },
  /**
   * Push these hours to the voice runtime and report what actually landed.
   *
   * The report is built from the runtime's own read-back, not from what was
   * sent: its day endpoint takes one window per call, and nothing says whether a
   * second call for the same day adds a window or replaces the first. `faithful`
   * false means cpass and the dialler disagree, which is the outcome that
   * matters and the one a plain "saved" would hide.
   */
  syncToRuntime: async (organizationId: string): Promise<APIResponse<BusinessHoursSyncReport>> => {
    try { const { data } = await axiosInstance.post('/business-hours/sync', null, { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to push the hours to the voice runtime' }; }
  },
  check: async (organizationId: string): Promise<APIResponse<BusinessHoursCheckResult>> => {
    try { const { data } = await axiosInstance.get('/business-hours/check', { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Check failed' }; }
  },
  isOpen: async (organizationId: string): Promise<APIResponse<any>> => {
    try { const { data } = await axiosInstance.get('/business-hours/is-open', { params: { organizationId } }); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Check failed' }; }
  },
};

export type CampaignType = 'OUTBOUND' | 'INBOUND' | 'HYBRID';
export type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ARCHIVED';

export interface CampaignDTO {
  id?: number;
  organizationId?: string;
  campaignName: string;
  description?: string;
  campaignType: CampaignType;
  status: CampaignStatus;
  startDate?: string;
  endDate?: string;
  providerConfig?: Record<string, any>;
  agentConfig?: Record<string, any>;
  maxConcurrentCalls?: number;
  maxCallDelay?: number;
  callsPerMinute?: number;
  overrideBusinessHours?: boolean;
  /**
   * Whether the same number may be queued on this campaign more than once.
   * Enforced by the backend (CampaignQueueService), so this is a reflection of
   * the rule rather than the rule itself.
   */
  allowDuplicateRecipients?: boolean;
  retryConfig?: Record<string, any>;
  totalScheduledCalls?: number;
  completedCalls?: number;
  failedCalls?: number;
  inProgressCalls?: number;
  successRate?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CampaignStatisticsDTO {
  campaignId: number;
  campaignName: string;
  totalScheduledCalls: number;
  completedCalls: number;
  failedCalls: number;
  inProgressCalls: number;
  pendingCalls: number;
  successRate: number;
  failureRate: number;
  avgCallDuration?: number;
}
export const campaignApi = {
  /**
   * Get all campaigns for organization
   * GET /campaigns?organizationId=xxx
   */
  getAll: async (organizationId: string): Promise<APIResponse<CampaignDTO[]>> =>
    apiCall<CampaignDTO[]>(
      () => axiosInstance.get('/campaigns', { params: { organizationId } }),
      'Failed to load campaigns'
    ),

  /**
   * Get campaign by ID
   * GET /campaigns/{id}?organizationId=xxx
   */
  getById: async (organizationId: string, id: number): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.get(`/campaigns/${id}`, { params: { organizationId } }),
      'Failed to load campaign'
    ),

  /**
   * Create campaign
   * POST /campaigns?organizationId=xxx
   */
  create: async (organizationId: string, campaign: {
    campaignName: string;
    description?: string;
    campaignType: CampaignType;
    startDate?: string;
    endDate?: string;
    providerConfig?: Record<string, any>;
    agentConfig?: Record<string, any>;
    maxConcurrentCalls?: number;
    maxCallDelay?: number;
    callsPerMinute?: number;
    overrideBusinessHours?: boolean;
    allowDuplicateRecipients?: boolean;
    retryConfig?: Record<string, any>;
  }): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.post('/campaigns', campaign, { params: { organizationId } }),
      'Failed to create campaign'
    ),

  /**
   * Update campaign
   * PUT /campaigns/{id}?organizationId=xxx
   */
  update: async (organizationId: string, id: number, campaign: Partial<{
    campaignName: string;
    description: string;
    campaignType: CampaignType;
    startDate: string;
    endDate: string;
    providerConfig: Record<string, any>;
    agentConfig: Record<string, any>;
    maxConcurrentCalls: number;
    maxCallDelay: number;
    callsPerMinute: number;
    overrideBusinessHours: boolean;
    retryConfig: Record<string, any>;
  }>): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.put(`/campaigns/${id}`, campaign, { params: { organizationId } }),
      'Failed to update campaign'
    ),

  /**
   * Delete campaign
   * DELETE /campaigns/{id}?organizationId=xxx
   */
  delete: async (organizationId: string, id: number): Promise<APIResponse<void>> =>
    apiCall<void>(
      () => axiosInstance.delete(`/campaigns/${id}`, { params: { organizationId } }),
      'Failed to delete campaign'
    ),

  /**
   * Activate campaign
   * POST /campaigns/{id}/activate?organizationId=xxx
   */
  activate: async (organizationId: string, id: number): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.post(`/campaigns/${id}/activate`, undefined, { params: { organizationId } }),
      'Failed to activate campaign'
    ),

  /**
   * Pause campaign
   * POST /campaigns/{id}/pause?organizationId=xxx
   */
  pause: async (organizationId: string, id: number): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.post(`/campaigns/${id}/pause`, undefined, { params: { organizationId } }),
      'Failed to pause campaign'
    ),

  /**
   * Resume campaign
   * POST /campaigns/{id}/resume?organizationId=xxx
   */
  resume: async (organizationId: string, id: number): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.post(`/campaigns/${id}/resume`, undefined, { params: { organizationId } }),
      'Failed to resume campaign'
    ),

  /**
   * Complete campaign
   * POST /campaigns/{id}/complete?organizationId=xxx
   */
  complete: async (organizationId: string, id: number): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.post(`/campaigns/${id}/complete`, undefined, { params: { organizationId } }),
      'Failed to complete campaign'
    ),

  /**
   * Archive campaign
   * POST /campaigns/{id}/archive?organizationId=xxx
   */
  archive: async (organizationId: string, id: number): Promise<APIResponse<CampaignDTO>> =>
    apiCall<CampaignDTO>(
      () => axiosInstance.post(`/campaigns/${id}/archive`, undefined, { params: { organizationId } }),
      'Failed to archive campaign'
    ),

  /**
   * Get campaign statistics
   * GET /campaigns/{id}/statistics?organizationId=xxx
   */
  getStatistics: async (organizationId: string, id: number): Promise<APIResponse<CampaignStatisticsDTO>> =>
    apiCall<CampaignStatisticsDTO>(
      () => axiosInstance.get(`/campaigns/${id}/statistics`, { params: { organizationId } }),
      'Failed to load campaign statistics'
    ),

  /**
   * Search campaigns
   * GET /campaigns/search?organizationId=xxx&...
   *
   * statuses repeats its key (statuses=A&statuses=B), which is what Spring
   * binds to a List. Axios's default serializer would send statuses[]=A, so the
   * query is built here and handed over as a URLSearchParams for axios to use
   * verbatim.
   */
  search: async (
    organizationId: string,
    params?: {
      nameContains?: string;
      statuses?: CampaignStatus[];
      startAfter?: string;
      endBefore?: string;
      page?: number;
      size?: number;
    }
  ): Promise<APIResponse<{ content: CampaignDTO[]; totalElements: number; totalPages: number }>> => {
    const queryParams = new URLSearchParams({
      organizationId,
      ...(params?.nameContains && { nameContains: params.nameContains }),
      ...(params?.startAfter && { startAfter: params.startAfter }),
      ...(params?.endBefore && { endBefore: params.endBefore }),
      ...(params?.page !== undefined && { page: String(params.page) }),
      ...(params?.size !== undefined && { size: String(params.size) }),
    });

    // Handle array parameter for statuses
    if (params?.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => queryParams.append('statuses', status));
    }

    return apiCall(
      () => axiosInstance.get('/campaigns/search', { params: queryParams }),
      'Failed to search campaigns'
    );
  },
};

// ----------------------------------------------------------------------------
// IVR Flow Management API (for IvrFlowController)
// ----------------------------------------------------------------------------

export const ivrFlowApi = {
  // Persists the editable flow graph on the cpass IvrApp registry (keyed by operator_id +
  // service_id), via the authenticated axios client. The runtime source of truth stays
  // state_details (written by Deploy); this lets the builder save & reload the exact graph.
  _saveFlow: async (flow: any): Promise<APIResponse<any>> => {
    const op = flow?.settings?.operator_id;
    const svc = flow?.settings?.service_id;
    if (!op || !svc) return { success: false, error: 'operator_id and service_id are required to save' };
    try {
      const { data } = await axiosInstance.post('/telephony/ivr/apps', {
        operator_id: op,
        service_id: svc,
        name: flow.flowName,
        description: flow.description,
        business_name: flow.settings?.business_name,
        languages: flow.settings?.language,
        status: flow.status || 'DRAFT',
        source: 'EDITOR',
        flow_graph: JSON.stringify(flow),
      });
      return data;
    } catch (e: any) {
      return { success: false, error: e?.response?.data?.message || 'Failed to save IVR flow' };
    }
  },

  /** Get a saved flow by the IvrApp id; parses flow_graph back into the editor's flow shape. */
  getById: async (_organizationId: string, id: number): Promise<APIResponse<any>> => {
    try {
      const { data } = await axiosInstance.get(`/telephony/ivr/apps/${id}`);
      const app = data?.data || data;
      if (!app) return { success: false, error: 'App not found' };
      const flow = app.flow_graph
        ? JSON.parse(app.flow_graph)
        : { flowName: app.name, nodes: [], settings: { operator_id: app.operator_id, service_id: app.service_id, language: app.languages } };
      return { success: true, data: flow };
    } catch (e: any) {
      return { success: false, error: e?.response?.data?.message || 'Failed to load IVR flow' };
    }
  },

  create: async function (_organizationId: string, flow: any): Promise<APIResponse<any>> {
    return this._saveFlow(flow);
  },

  update: async function (_organizationId: string, _id: number, flow: any): Promise<APIResponse<any>> {
    return this._saveFlow(flow);
  },

  delete: async (_organizationId: string, id: number): Promise<APIResponse<void>> => {
    try { const { data } = await axiosInstance.delete(`/telephony/ivr/apps/${id}`); return data; }
    catch (e: any) { return { success: false, error: e?.response?.data?.message || 'Failed to delete' }; }
  },

  /**
   * Activate IVR flow
   */
  activate: async (organizationId: string, id: number): Promise<APIResponse<any>> => {
    return fetchAPI<any>(`/ivr/flows/${id}/activate?organizationId=${encodeURIComponent(organizationId)}`, {
      method: 'POST',
    });
  },

  /**
   * Search IVR flows
   */
  search: async (
    organizationId: string,
    params?: {
      flowName?: string;
      status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE';
      enabled?: boolean;
      page?: number;
      size?: number;
    }
  ): Promise<APIResponse<any>> => {
    const queryParams = new URLSearchParams({
      organizationId,
      ...(params?.flowName && { flowName: params.flowName }),
      ...(params?.status && { status: params.status }),
      ...(params?.enabled !== undefined && { enabled: String(params.enabled) }),
      ...(params?.page !== undefined && { page: String(params.page) }),
      ...(params?.size !== undefined && { size: String(params.size) }),
    });
    return fetchAPI<any>(`/ivr/flows/search?${queryParams.toString()}`);
  },
};

// ----------------------------------------------------------------------------
// OBD (Outbound Dialer) Campaign Management API
// ----------------------------------------------------------------------------

export type OBDCampaignStatus = 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
export type OBDCampaignType = 'VOICE_BLAST' | 'PRESS_1' | 'IVR_SURVEY' | 'AI_AGENT';
export type OBDRetryStrategy = 'NONE' | 'FIXED_INTERVAL' | 'EXPONENTIAL_BACKOFF';

export interface OBDCampaignDTO {
  id?: number;
  organizationId?: string;
  campaignName: string;
  description?: string;
  campaignType: OBDCampaignType;
  status: OBDCampaignStatus;
  audioFileUrl?: string;
  ttsMessage?: string;
  ivrFlowId?: number;
  aiAgentId?: number;
  callerId?: string;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  timezone?: string;
  maxConcurrentCalls?: number;
  callsPerMinute?: number;
  maxAttempts?: number;
  retryStrategy?: OBDRetryStrategy;
  retryDelayMinutes?: number;
  respectBusinessHours?: boolean;
  respectDND?: boolean;
  contactListId?: number;
  totalContacts?: number;
  totalAttempts?: number;
  completedCalls?: number;
  answeredCalls?: number;
  failedCalls?: number;
  busyCalls?: number;
  noAnswerCalls?: number;
  voicemailCalls?: number;
  callbackRequests?: number;
  answerRate?: number;
  completionRate?: number;
  avgCallDuration?: number;
  totalCost?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface OBDContactListDTO {
  id?: number;
  organizationId?: string;
  listName: string;
  description?: string;
  totalContacts?: number;
  validContacts?: number;
  invalidContacts?: number;
  duplicateContacts?: number;
  createdAt?: string;
}

export interface OBDContactDTO {
  id?: number;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
  customFields?: Record<string, any>;
  status?: 'PENDING' | 'CALLED' | 'ANSWERED' | 'NO_ANSWER' | 'BUSY' | 'FAILED' | 'VOICEMAIL' | 'CALLBACK';
  attempts?: number;
  lastAttemptAt?: string;
  callDuration?: number;
  callResult?: string;
}

// OBD stays hidden behind FEATURE_FLAGS.SHOW_OBD_CAMPAIGNS. It is migrated
// anyway because it shares this file's defect verbatim — every call unauthenticated,
// every 401 invisible to the interceptor — and a half-migrated file is the kind
// of thing that gets re-broken by the next person. Nothing here unhides it.
export const obdCampaignApi = {
  /**
   * Get all OBD campaigns for organization
   * GET /obd/campaigns?organizationId=xxx
   */
  getAll: async (organizationId: string): Promise<APIResponse<OBDCampaignDTO[]>> =>
    apiCall<OBDCampaignDTO[]>(
      () => axiosInstance.get('/obd/campaigns', { params: { organizationId } }),
      'Failed to load OBD campaigns'
    ),

  /**
   * Get OBD campaign by ID
   * GET /obd/campaigns/{id}?organizationId=xxx
   */
  getById: async (organizationId: string, id: number): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.get(`/obd/campaigns/${id}`, { params: { organizationId } }),
      'Failed to load OBD campaign'
    ),

  /**
   * Create OBD campaign
   * POST /obd/campaigns?organizationId=xxx
   */
  create: async (organizationId: string, campaign: {
    campaignName: string;
    description?: string;
    campaignType: OBDCampaignType;
    audioFileUrl?: string;
    ttsMessage?: string;
    ivrFlowId?: number;
    aiAgentId?: number;
    callerId?: string;
    scheduledStartTime?: string;
    scheduledEndTime?: string;
    timezone?: string;
    maxConcurrentCalls?: number;
    callsPerMinute?: number;
    maxAttempts?: number;
    retryStrategy?: OBDRetryStrategy;
    retryDelayMinutes?: number;
    respectBusinessHours?: boolean;
    respectDND?: boolean;
    contactListId?: number;
  }): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.post('/obd/campaigns', campaign, { params: { organizationId } }),
      'Failed to create OBD campaign'
    ),

  /**
   * Update OBD campaign
   * PUT /obd/campaigns/{id}?organizationId=xxx
   */
  update: async (organizationId: string, id: number, campaign: Partial<{
    campaignName: string;
    description: string;
    campaignType: OBDCampaignType;
    audioFileUrl: string;
    ttsMessage: string;
    ivrFlowId: number;
    aiAgentId: number;
    callerId: string;
    scheduledStartTime: string;
    scheduledEndTime: string;
    timezone: string;
    maxConcurrentCalls: number;
    callsPerMinute: number;
    maxAttempts: number;
    retryStrategy: OBDRetryStrategy;
    retryDelayMinutes: number;
    respectBusinessHours: boolean;
    respectDND: boolean;
    contactListId: number;
  }>): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.put(`/obd/campaigns/${id}`, campaign, { params: { organizationId } }),
      'Failed to update OBD campaign'
    ),

  /**
   * Delete OBD campaign
   * DELETE /obd/campaigns/{id}?organizationId=xxx
   */
  delete: async (organizationId: string, id: number): Promise<APIResponse<void>> =>
    apiCall<void>(
      () => axiosInstance.delete(`/obd/campaigns/${id}`, { params: { organizationId } }),
      'Failed to delete OBD campaign'
    ),

  /**
   * Start OBD campaign
   * POST /obd/campaigns/{id}/start?organizationId=xxx
   */
  start: async (organizationId: string, id: number): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.post(`/obd/campaigns/${id}/start`, undefined, { params: { organizationId } }),
      'Failed to start OBD campaign'
    ),

  /**
   * Pause OBD campaign
   * POST /obd/campaigns/{id}/pause?organizationId=xxx
   */
  pause: async (organizationId: string, id: number): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.post(`/obd/campaigns/${id}/pause`, undefined, { params: { organizationId } }),
      'Failed to pause OBD campaign'
    ),

  /**
   * Resume OBD campaign
   * POST /obd/campaigns/{id}/resume?organizationId=xxx
   */
  resume: async (organizationId: string, id: number): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.post(`/obd/campaigns/${id}/resume`, undefined, { params: { organizationId } }),
      'Failed to resume OBD campaign'
    ),

  /**
   * Cancel OBD campaign
   * POST /obd/campaigns/{id}/cancel?organizationId=xxx
   */
  cancel: async (organizationId: string, id: number): Promise<APIResponse<OBDCampaignDTO>> =>
    apiCall<OBDCampaignDTO>(
      () => axiosInstance.post(`/obd/campaigns/${id}/cancel`, undefined, { params: { organizationId } }),
      'Failed to cancel OBD campaign'
    ),

  /**
   * Get campaign statistics
   * GET /obd/campaigns/{id}/stats?organizationId=xxx
   */
  getStats: async (organizationId: string, id: number): Promise<APIResponse<{
    totalContacts: number;
    completedCalls: number;
    answeredCalls: number;
    failedCalls: number;
    pendingCalls: number;
    answerRate: number;
    completionRate: number;
    avgCallDuration: number;
    callsByStatus: Record<string, number>;
    callsByHour: Array<{ hour: string; count: number }>;
  }>> =>
    apiCall(
      () => axiosInstance.get(`/obd/campaigns/${id}/stats`, { params: { organizationId } }),
      'Failed to load OBD campaign statistics'
    ),

  /**
   * Search OBD campaigns
   * GET /obd/campaigns/search?organizationId=xxx&...
   *
   * As with campaignApi.search, statuses has to repeat its key for Spring to
   * bind a List, so the query is built here rather than by axios.
   */
  search: async (
    organizationId: string,
    params?: {
      nameContains?: string;
      statuses?: OBDCampaignStatus[];
      campaignType?: OBDCampaignType;
      startAfter?: string;
      endBefore?: string;
      page?: number;
      size?: number;
    }
  ): Promise<APIResponse<{ content: OBDCampaignDTO[]; totalElements: number; totalPages: number }>> => {
    const queryParams = new URLSearchParams({
      organizationId,
      ...(params?.nameContains && { nameContains: params.nameContains }),
      ...(params?.campaignType && { campaignType: params.campaignType }),
      ...(params?.startAfter && { startAfter: params.startAfter }),
      ...(params?.endBefore && { endBefore: params.endBefore }),
      ...(params?.page !== undefined && { page: String(params.page) }),
      ...(params?.size !== undefined && { size: String(params.size) }),
    });

    if (params?.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => queryParams.append('statuses', status));
    }

    return apiCall(
      () => axiosInstance.get('/obd/campaigns/search', { params: queryParams }),
      'Failed to search OBD campaigns'
    );
  },
};

// ----------------------------------------------------------------------------
// OBD Contact List Management API
// ----------------------------------------------------------------------------

export const obdContactListApi = {
  /**
   * Get all contact lists for organization
   */
  getAll: async (organizationId: string): Promise<APIResponse<OBDContactListDTO[]>> =>
    apiCall<OBDContactListDTO[]>(
      () => axiosInstance.get('/obd/contact-lists', { params: { organizationId } }),
      'Failed to load contact lists'
    ),

  /**
   * Create contact list
   */
  create: async (organizationId: string, list: {
    listName: string;
    description?: string;
  }): Promise<APIResponse<OBDContactListDTO>> =>
    apiCall<OBDContactListDTO>(
      () => axiosInstance.post('/obd/contact-lists', list, { params: { organizationId } }),
      'Failed to create contact list'
    ),

  /**
   * Upload contacts to list via CSV
   */
  uploadContacts: async (organizationId: string, listId: number, file: File): Promise<APIResponse<{
    imported: number;
    failed: number;
    duplicates: number;
    errors: string[];
  }>> => {
    const formData = new FormData();
    formData.append('file', file);

    return apiCall(
      () => axiosInstance.post(`/obd/contact-lists/${listId}/upload`, formData, {
        params: { organizationId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      'Upload failed'
    );
  },

  /**
   * Delete contact list
   */
  delete: async (organizationId: string, id: number): Promise<APIResponse<void>> =>
    apiCall<void>(
      () => axiosInstance.delete(`/obd/contact-lists/${id}`, { params: { organizationId } }),
      'Failed to delete contact list'
    ),
};

// ----------------------------------------------------------------------------
// Export All
// ----------------------------------------------------------------------------

export default {
  ivr: ivrApi,
  ivrFlow: ivrFlowApi,
  organization: organizationApi,
  business: businessApi,
  businessHours: businessHoursApi,
  campaign: campaignApi,
  obdCampaign: obdCampaignApi,
  obdContactList: obdContactListApi,
  vipList: vipListApi,
  vip: vipApi,
  dndList: dndListApi,
  dnd: dndApi,
  calls: callsApi,
  audio: audioApi,
};
